import React, { useCallback } from 'react';
import { LocationModel } from 'models/CompanySetup/location.model';
import { AddressModel } from 'models/CompanySetup/address.model';
import clsx from 'clsx';
import styles from '../Modals/LocationModal.module.scss';
import {
  LocationTableStrings,
  LocationTableFields,
} from 'enums/CompanySetupEnums';
import { URLSearchParamsInit } from 'react-router-dom';
import { SortingTypesEnums } from 'enums/sortingTypes.enum';
interface LocationTableColumnsProps {
  sortOrder: {
    field: string;
    order: SortingTypesEnums.DESCEND | SortingTypesEnums.ASCEND | undefined;
  };
  setSortOrder: React.Dispatch<
    React.SetStateAction<{
      field: string;
      order: SortingTypesEnums.DESCEND | SortingTypesEnums.ASCEND | undefined;
    }>
  >;
  searchParams: URLSearchParams;
  setSearchParams: (params: URLSearchParamsInit) => void;
}
const LocationTableColumns = ({
  sortOrder,
  setSortOrder,
  searchParams,
  setSearchParams,
}: LocationTableColumnsProps) => {
  const handleSort = useCallback(
    fieldName => {
      const isAscend =
        sortOrder.field === fieldName &&
        sortOrder.order === SortingTypesEnums.ASCEND;
      const newOrder = isAscend
        ? SortingTypesEnums.DESCEND
        : SortingTypesEnums.ASCEND;
      setSortOrder({ field: fieldName, order: newOrder });

      const currentSearchParams = new URLSearchParams(window.location.search);
      currentSearchParams.set('sort_by', fieldName);
      currentSearchParams.set('sort_dir', newOrder);
      setSearchParams(currentSearchParams);
    },
    [sortOrder, searchParams]
  );

  return [
    {
      title: LocationTableStrings.LOCATION_NAME,
      dataIndex: LocationTableFields.NAME,
      sorter: true,
      sortOrder:
        sortOrder.field === LocationTableFields.NAME
          ? sortOrder.order
          : undefined,
      onHeaderCell: () => ({
        onClick: () => handleSort(LocationTableFields.NAME),
      }),
    },
    {
      title: LocationTableStrings.ORGANIZATION,
      dataIndex: [
        LocationTableFields.ORGANIZATION,
        LocationTableFields.ORGANIZATION_NAME,
      ],
      sorter: true,
      sortOrder:
        sortOrder.field ===
        `${LocationTableFields.ORGANIZATION}.${LocationTableFields.ORGANIZATION_NAME}`
          ? sortOrder.order
          : undefined,
      onHeaderCell: () => ({
        onClick: () =>
          handleSort(
            `${LocationTableFields.ORGANIZATION}.${LocationTableFields.ORGANIZATION_NAME}`
          ),
      }),
      render: (name: string) => <div className="text-capitalize">{name}</div>,
    },
    {
      title: LocationTableStrings.ORGANIZATION_BOUNDARY,
      dataIndex: LocationTableFields.SHAREERCENTAGE,
      sorter: true,
      sortOrder:
        sortOrder.field === LocationTableFields.SHAREPERCENTAGE
          ? sortOrder.order
          : undefined,
      onHeaderCell: () => ({
        onClick: () => handleSort(LocationTableFields.SHAREPERCENTAGE),
      }),
      render: (val: string) => (
        <div>
          {val ? val : 0} {LocationTableStrings.PERCENTAGE_SIGN}
        </div>
      ),
    },
    {
      title: LocationTableStrings.TYPE,
      dataIndex: LocationTableFields.CATEGORY,
      sorter: true,
      sortOrder:
        sortOrder.field === LocationTableFields.CATEGORY
          ? sortOrder.order
          : undefined,
      onHeaderCell: () => ({
        onClick: () => handleSort(LocationTableFields.CATEGORY),
      }),
    },
    {
      title: LocationTableStrings.ADDRESS,
      dataIndex: LocationTableFields.ADDRESS,
      sorter: true,
      sortOrder:
        sortOrder.field === LocationTableFields.FULLADDRESS
          ? sortOrder.order
          : undefined,
      onHeaderCell: () => ({
        onClick: () => handleSort(LocationTableFields.FULLADDRESS),
      }),
      render: (address: AddressModel) => <div>{address.getFullAddress()}</div>,
    },
    {
      title: LocationTableStrings.ACTIONS,
      width: 150,
      sorter: true,
      sortOrder:
        sortOrder.field === LocationTableFields.STATUS
          ? sortOrder.order
          : undefined,
      onHeaderCell: () => ({
        onClick: () => handleSort(LocationTableFields.STATUS),
      }),
      render: (_text: string, record: LocationModel) => (
        <div
          className={clsx(styles.status, {
            [styles.statusComplete]: record.isCompleted,
            [styles.statusIncomplete]: !record.isCompleted,
          })}>
          {record.isCompleted
            ? LocationTableStrings.COMPLETED
            : LocationTableStrings.INCOMPLETE}
        </div>
      ),
    },
  ];
};

export default LocationTableColumns;
